import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import ILEARNCertificate from "../../assets/image/ilearnCertificate.jpg";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import API from "../../util/api";

// Styled Components
const DocumentListBlock = styled.div`
  overflow: hidden;
  padding: 10px;
  width: 100%;
  width: 1050px;
  margin: 0 auto;
`;

const DocumentListMenu = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  font-size: 1rem;
  font-weight: bold;
  padding: 10px;
  margin-bottom: 0;
  border-radius: 5px 5px 0 0;
  color: white;
  background-color: ${(props) => props.colors.componentHeaderBackground};
`;

const ProgressTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 10px;
    border: none;
  }

  th {
    background-color: #fff;
    color: ${(props) => props.colors.buttonBackground};
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #fff;
  }

  tr:nth-child(odd) {
    background-color: #f0f0f0;
  }

  td {
    color: ${(props) => props.colors.cellTextColor || "#000"};
  }

  .button {
    padding: 5px 10px;
    background-color: ${(props) => props.colors.buttonBackground};
    color: ${(props) => props.colors.buttonText || "#fff"};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;

    &:hover {
      background-color: ${(props) =>
        props.colors.buttonHoverBackground};
    }
  }
`;

const CanvasContainer = styled.div`
  display: none;
`;

const Loading = styled.p`
  font-style: italic;
  text-align: center;
`;

// Main Component
export default function CertificateListing({ colors }) {
  const canvasRef = useRef(null);
  const firstName = useSelector((state) => state.auth.user.first_name);
  const lastName = useSelector((state) => state.auth.user.last_name);
  const user_id = useSelector((state) => state.auth.user.user_id);

  const [certData, setCertData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch last watched chapter for module 6
  useEffect(() => {
    API.getUserCertificate({ user_id, module_no: 6 })
      .then((resp) => {
        if (
          resp.status === "Success" &&
          Array.isArray(resp.result) &&
          resp.result.length > 0
        ) {
          setCertData(resp.result[0]);
        }
      })
      .catch((err) => {
        console.error("Failed to load certification data:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user_id]);

  const getCertificateDate = () => {
    if (!certData) return "";

    const rawDate = certData.certificate_date || certData.watch_date || "";
    return rawDate.split(" ")[0];
  };

  const handleDownloadCertificate = () => {
    if (!certData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const blankCertificate = new Image();
    blankCertificate.src = ILEARNCertificate;

    blankCertificate.onload = () => {
      canvas.width = blankCertificate.width;
      canvas.height = blankCertificate.height;
      ctx.drawImage(blankCertificate, 0, 0);

      // Draw user name
      const userName = `${firstName} ${lastName}`;
      ctx.font = "bold 50px Arial";
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.fillText(userName, canvas.width / 2, canvas.height / 2 - 20);

      // Draw "PDP Module"
      ctx.font = "italic 30px Arial";
      ctx.fillText("PDP Module", canvas.width / 2, canvas.height / 2 + 35);

      // Draw date
      const watchDate = getCertificateDate();
      ctx.font = "10px Arial";
      ctx.textAlign = "right";
      ctx.fillText(
        watchDate,
        canvas.width / 2 - 10,
        canvas.height / 2 + 115
      );

      // Draw duration
      ctx.font = "20px Arial";
      ctx.fillText(
        "Training Duration: 6 hours",
        canvas.width - 60,
        canvas.height - 70
      );

      // Export PDF
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width * 0.5, canvas.height * 0.5],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width * 0.5, canvas.height * 0.5);
      pdf.save(`${userName}_Certificate.pdf`);
    };
  };

  const hasCertified = certData && Number(certData.chapter_no) >= 7;

  return (
    <DocumentListBlock>
      <DocumentListMenu>
        <SectionHeader colors={colors}>Certificates</SectionHeader>
        
        {loading ? (
          <Loading>Loading certification status…</Loading>
        ) : (
          <ProgressTable colors={colors}>
            <thead>
              <tr>
                <th>#</th>
                <th>Event Date</th>
                <th>Event ID</th>
                <th>Facilitator</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {hasCertified ? (
                <tr>
                  <td>1</td>
                  <td>{getCertificateDate()}</td>
                  <td></td>
                  <td>Online</td>
                  <td>
                    <button
                      className="button"
                      onClick={handleDownloadCertificate}
                    >
                      Certificate
                    </button>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "1em" }}>
                    You have not yet completed the ILEARN certification.
                  </td>
                </tr>
              )}
            </tbody>
          </ProgressTable>
        )}
      </DocumentListMenu>
      <CanvasContainer>
        <canvas ref={canvasRef} />
      </CanvasContainer>
    </DocumentListBlock>
  );
}
